/**
 * @file        src/components/BookingTimeline.jsx
 * @description Per-booking operational history (Details drawer → Timeline card).
 *              Reads GET /<kind>/bookings/:id/logs, which returns ONE ordered
 *              `timeline` array composed server-side (bookingTimeline.service):
 *              created → payment (incl. wallet, and post-booking seat/meal/
 *              baggage) → amendment → cancellation → refund initiated →
 *              refund completed, plus the admin console's own edits.
 *              Every event renders its timestamp, its ACTOR (customer / admin /
 *              system) and its per-field before → after changes. Shared by the
 *              flight and hotel drawers.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-08-04 Claude — Initial (booking timeline, flight + hotel).
 *   - 2026-09-09 Claude — Renders the server-composed `timeline` (was: merging
 *                          `entries` + `logs` here, which is why payment and
 *                          refund events — written by the customer-facing
 *                          backend, not booking_logs — never appeared). Shows
 *                          the actor type and a before → after row per changed
 *                          field instead of only a status pair.
 */
import { useQuery } from '@tanstack/react-query';
import { getBookingLogs as flightLogs } from '../api/flightBookings';
import { getBookingLogs as hotelLogs } from '../api/hotelBookings';

// The server sends a human title with every event; this is the fallback for an
// event type added server-side before this file knows about it.
const FALLBACK_LABEL = {
  created: 'Booking created',
  payment: 'Payment received',
  modified: 'Booking updated',
  amendment_requested: 'Amendment requested',
  cancel_requested: 'Cancellation requested',
  cancel_confirmed: 'Cancellation confirmed',
  refund_initiated: 'Refund initiated',
  refund_completed: 'Refund completed',
};

const TONE = {
  payment: 'good',
  payment_wallet: 'good',
  payment_ssr: 'good',
  payment_reissue: 'good',
  payment_failed: 'danger',
  payment_refunded: 'warn',
  amendment_requested: 'warn',
  cancel_requested: 'warn',
  cancel_confirmed: 'danger',
  refund_initiated: 'warn',
  refund_approved: 'warn',
  refund_processed: 'warn',
  refund_completed: 'good',
  refund_rejected: 'danger',
};

const ACTOR = {
  customer: 'Customer',
  admin: 'Admin',
  system: 'System',
};

function dt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function actorLabel(e) {
  const who = ACTOR[e.actor_type] || 'System';
  return e.actor_type === 'admin' && e.actor_id ? `${who} #${e.actor_id}` : who;
}

export default function BookingTimeline({ kind, bookingId }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['booking-logs', kind, bookingId],
    queryFn: () => (kind === 'hotel' ? hotelLogs(bookingId) : flightLogs(bookingId)),
    enabled: !!bookingId,
  });

  const items = data?.data?.timeline || [];

  return (
    <>
      <h4 className="det-h">Timeline</h4>
      {isLoading ? <div className="muted">Loading history…</div>
        : isError ? <div className="muted">Could not load the booking history.</div>
        : items.length === 0 ? <div className="muted">No history recorded for this booking.</div>
        : (
        <ul className="tl-list">
          {items.map((e, i) => (
            <li className="tl-item" key={e.key ?? `e${i}`}>
              <span className={`tl-dot ${TONE[e.log_type] || ''}`} />
              <div className="tl-head">
                <b>{e.title || FALLBACK_LABEL[e.log_type] || e.log_type || 'Event'}</b>
                <span className="tl-when">{dt(e.created_at)}</span>
                <span className="tl-when">· {actorLabel(e)}</span>
              </div>
              {e.description ? <div className="tl-detail">{e.description}</div> : null}
              {(e.changes || []).map((c, ci) => (
                <div className="tl-change" key={`${e.key}-c${ci}`}>
                  <span className="tl-field">{c.label || c.field}</span>
                  <span className="tl-from">{c.before ?? '—'}</span>
                  <span className="tl-arrow" aria-label="changed to">→</span>
                  <span className="tl-to">{c.after ?? '—'}</span>
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
